export interface EditLocator {
  scope: string;
  id?: number;
  field: string;
}

export interface EnvelopeEntry {
  locator: EditLocator;
  before: string;
  after: string;
  mode?: string;
  [key: string]: unknown;
}

export interface FieldGroup {
  key: string;
  locator: EditLocator;
  entries: EnvelopeEntry[];
  indexes: number[];
}

export interface FieldDraft {
  live: string;
  projected: string;
  draft: string;
}

export function isPureEditsEnvelope(patch: unknown): patch is { _edits: EnvelopeEntry[] } {
  return !!patch && typeof patch === 'object' && !Array.isArray(patch)
    && Object.keys(patch).length === 1 && Object.hasOwn(patch, '_edits')
    && Array.isArray((patch as { _edits?: unknown })._edits)
    && (patch as { _edits: unknown[] })._edits.length > 0;
}

export function fieldKey(locator: EditLocator): string {
  return JSON.stringify([locator.scope, locator.id ?? null, locator.field]);
}

export function groupEnvelopeFields(edits: EnvelopeEntry[]): FieldGroup[] {
  const groups = new Map<string, FieldGroup>();
  edits.forEach((entry, index) => {
    const key = fieldKey(entry.locator);
    let group = groups.get(key);
    if (!group) {
      group = { key, locator: entry.locator, entries: [], indexes: [] };
      groups.set(key, group);
    }
    group.entries.push(entry);
    group.indexes.push(index);
  });
  return [...groups.values()];
}

export type LiveField = { state: 'found'; value: string }
  | { state: 'missing' | 'ambiguous' | 'not_text'; reason: string };

export function readLiveField(doc: unknown, locator: EditLocator): LiveField {
  if (!doc || typeof doc !== 'object') return { state: 'missing', reason: 'the live document is missing' };
  const record = doc as Record<string, unknown>;
  let owner: Record<string, unknown>;
  if (locator.scope === 'document') {
    owner = record;
  } else if (locator.scope === 'step' || locator.scope === 'response') {
    const collection = record[locator.scope === 'step' ? 'steps' : 'responses'];
    if (!Array.isArray(collection)) return { state: 'missing', reason: `the live ${locator.scope} list is missing` };
    const matches = collection.filter((value) => value && typeof value === 'object'
      && typeof value.id === 'number' && value.id === locator.id);
    if (matches.length > 1) return { state: 'ambiguous', reason: `more than one ${locator.scope} has id ${locator.id}` };
    if (matches.length === 0) return { state: 'missing', reason: `${locator.scope} ${locator.id} is missing` };
    owner = matches[0] as Record<string, unknown>;
  } else {
    return { state: 'missing', reason: 'the location is unknown' };
  }
  if (!Object.hasOwn(owner, locator.field)) return { state: 'missing', reason: 'the field is missing' };
  if (typeof owner[locator.field] !== 'string') return { state: 'not_text', reason: 'the field is not text' };
  return { state: 'found', value: owner[locator.field] as string };
}

const normalizeNewlines = (text: string) => text.replace(/\r\n?/g, '\n');
const label = (locator: EditLocator) => locator.id === undefined
  ? locator.field : `${locator.scope} ${locator.id} · ${locator.field}`;

export function buildReviewerEnvelope(
  edits: EnvelopeEntry[],
  drafts: Record<string, FieldDraft>,
): { ok: true; patch: { _edits: EnvelopeEntry[] } } | { ok: false; message: string } {
  const groups = groupEnvelopeFields(edits);
  const changed = new Map<string, FieldDraft>();
  for (const group of groups) {
    const draft = drafts[group.key];
    if (!draft || normalizeNewlines(draft.draft) === normalizeNewlines(draft.projected)) continue;
    if (draft.draft.replace(/[\s\p{Cf}]/gu, '') === '') return { ok: false, message: `The new text for ${label(group.locator)} is empty. Blanking a field is not something an edit here can do.` };
    if (normalizeNewlines(draft.draft) === normalizeNewlines(draft.live)) return { ok: false, message: `The new text for ${label(group.locator)} is the same as the text learners see now, so nothing would change. To drop this correction, reject it with a comment.` };
    const consistentlyCrLf = draft.live.includes('\r\n') && !draft.live.replace(/\r\n/g, '').includes('\n');
    changed.set(group.key, consistentlyCrLf
      ? { ...draft, draft: normalizeNewlines(draft.draft).replace(/\n/g, '\r\n') }
      : draft);
  }
  if (changed.size === 0) return { ok: false, message: 'Nothing has been changed. To accept the correction as it is, use Approve.' };

  const first = new Map(groups.map((group) => [group.key, group.indexes[0]]));
  const rewritten: EnvelopeEntry[] = [];
  edits.forEach((entry, index) => {
    const key = fieldKey(entry.locator);
    const draft = changed.get(key);
    if (!draft) rewritten.push(entry);
    else if (first.get(key) === index) rewritten.push({ locator: { ...entry.locator }, before: draft.live, after: draft.draft, mode: 'whole' });
  });
  return { ok: true, patch: { _edits: rewritten } };
}
